import { useMutation, useQuery, UseQueryOptions } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { queryClient } from 'providers/AllProviders';
import {
  createIntraofficeNote,
  deleteIntraofficeNote,
  getIntraofficeNote,
  getIntraofficeNotes,
  updateIntraofficeNote
} from './api';
import { IntraofficeNoteRequest, IntraofficeNoteResponse } from './types';
import { MetaApiResponse } from 'utils/types/CommonApiResponse';

export function useGetIntraofficeNotes(
  params: any,
  options?: UseQueryOptions<{ data: IntraofficeNoteResponse[]; meta: MetaApiResponse }, Error>
) {
  return useQuery<{ data: IntraofficeNoteResponse[]; meta: MetaApiResponse }>({
    queryKey: ['agencyIntraofficeNotes', params],
    queryFn: () => getIntraofficeNotes(params),
    ...options
  });
}

export function useGetIntraofficeNote(options?: UseQueryOptions<IntraofficeNoteResponse, Error>) {
  const { noteId } = useParams<{ noteId: string }>();

  return useQuery<IntraofficeNoteResponse>({
    queryKey: ['agencyIntraofficeNote', noteId],
    queryFn: () => getIntraofficeNote(noteId!),
    enabled: Boolean(noteId),
    ...options
  });
}

export function useCreateOrUpdateIntraofficeNote(isDrawerClose?: boolean) {
  const navigate = useNavigate();
  const { noteId } = useParams<{ noteId: string }>();

  return useMutation({
    mutationFn: (payload: IntraofficeNoteRequest) =>
      noteId ? updateIntraofficeNote(noteId!, payload) : createIntraofficeNote(payload),

    onSuccess: (data) => {
      if (data !== undefined) {
        queryClient.invalidateQueries({
          queryKey: ['agencyIntraofficeNote'],
          refetchType: 'active'
        });

        queryClient.invalidateQueries({
          queryKey: ['agencyIntraofficeNotes'],
          refetchType: 'active'
        });

        if (isDrawerClose) navigate(-1);
      }
    }
  });
}

export function useDeleteIntraofficeNote() {
  return useMutation({
    mutationFn: (id: string) => deleteIntraofficeNote(id),

    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['agencyIntraofficeNotes'],
        refetchType: 'active'
      });
    }
  });
}
